import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    useSelector
} from 'react-redux';
import {enumerate} from 'ts-transformer-enumerate';

import {
    Dropdown,
    DropdownProps,
    DropdownChangeEvent
} from 'primereact/dropdown';

import {__} from '~/shared/src/utilities/messageCatalog';
import {StoreState} from '~/frontend/src/store';
import {
    GenericPermissionName,
    YnPermissionName
} from '~/shared/src/model/User';
import metaModel from '~/shared/src/utilities/metaModel';
import {DomHandler} from 'primereact/utils';


const schemata = enumerate<YnSchema>();


export type DefaultProps = {

    /**
     * The label for the field.
     */
    label? : string;

    /**
     * The permission to check for the ItemTypes of the schema displayed in the Dropdown.
     */
    permission? : YnPermissionName;

    /**
     * The generic permission to check for the ItemTypes of the schema displayed in the Dropdown.
     */
    genericPermission? : GenericPermissionName;

    /**
     * The CSS class name(s) to add to the component.
     */
    className? : string;

    inputStyle? : React.CSSProperties;
};
const defaultProps : Required<DefaultProps> = {
    label            : '',
    permission       : 'read',
    genericPermission: 'none',
    className        : '',
    inputStyle       : { width: '100%' }
};

type Props = Omit<DropdownProps, 'onChange' | 'onFocus' | 'onBlur'> & {
    /**
     * The uniqueId of the panel the component is used in.
     *
     * Mostly used to extract the components state from the global one
     * and to generate unique dom element ids.
     */
    uid : string;

    /**
     * The selected schema.
     */
    value : Schema;

    /**
     * Callback to invoke if value has changed.
     *
     * @param {String} schema
     */
    onChange : Function;

    style? : React.CSSProperties;
};

export type ComponentProps = DefaultProps & Props;
type AllProps = Required<DefaultProps> & Props & {
    onBlur? : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
    onFocus? : ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => void;
};


const getMobile = () => matchMedia( 'screen and (max-width: 960px)' ).matches ? true : false;


const SchemaDropdown = ( props : AllProps ) => {
    const user                                = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          {
              className,
              genericPermission,
              inputStyle,
              label,
              onBlur,
              onChange,
              onFocus,
              permission,
              style,
              tabIndex,
              uid,
              value,
              // catch all other props for dropdown component
              ...dropdownProps
          }                                   = props,
          dropdownComponentRef                = useRef<Dropdown>( null ),
          fieldLeftTimeoutId                  = useRef<NodeJS.Timeout | undefined>(),
          triggerJustFocused                  = useRef<boolean>( false ),
          [ mobile, setMobile ]               = useState<boolean>( getMobile() ),
          [ focus, setFocus ]                 = useState<boolean>( false ),
          [ overlay, setOverlay ]             = useState<boolean>( false ),
          [ renderTrigger, setRenderTrigger ] = useState<number>( 0 ),
          language                            = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          data                                = useMemo(
              () => {
                  const retVal : string[] = [];

                  for ( const schema of schemata ) {
                      const itemTypes = metaModel[ schema ].getItemTypes() ?? [];

                      for ( const itemType of itemTypes ) {
                          if (
                              itemType.id > 0
                              && user?.hasPermissionOnItemType( permission, itemType.id, schema )
                              && ( genericPermission === 'none' || user?.hasGenericPermissionOnItemType( genericPermission, itemType.id, schema ) )
                          ) {
                              retVal.push( schema );
                              break;
                          }
                      }
                  }

                  retVal.sort();

                  return retVal;
              },
              [
                  permission,
                  genericPermission,
                  schemata,
                  user
              ]
          ),
          dropdownClassNames                  = useMemo(
              () => {
                  const retVal = [ 'yn-inputgroup' ];

                  if ( !label ) {
                      retVal.push( className );
                  }
                  if ( focus || overlay ) {
                      retVal.push( 'p-focus' );
                  }

                  return retVal;
              },
              [
                  className,
                  focus,
                  label,
                  overlay,
                  value
              ]
          ),
          onDocumentKeyDown                   = useCallback(
              ( event : React.KeyboardEvent<HTMLElement> ) => {
                  if ( dropdownComponentRef.current ) {
                      let handled : boolean = false,
                          component : Dropdown | undefined,
                          element : HTMLElement | undefined,
                          overlayElement : HTMLElement | undefined,
                          downTrigger : HTMLElement | undefined;

                      switch ( event.key ) {

                          case 'Enter':
                              component = dropdownComponentRef.current;
                              element = component.getElement();
                              overlayElement = component.getOverlay();
                              downTrigger = element?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;

                              if ( event.target === downTrigger ) {
                                  handled = true;

                                  if ( overlayElement ) {
                                      // missing TypeScript definition for Dropdown.hide
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.hide();
                                  }
                                  else {
                                      // missing TypeScript definition for Dropdown.show
                                      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                                      // @ts-ignore
                                      component!.show();
                                  }
                              }

                              if ( handled ) {
                                  event.stopPropagation();
                                  event.preventDefault();
                              }

                              break;

                          case 'Escape':
                              if ( dropdownComponentRef.current?.getOverlay() ) {
                                  dropdownComponentRef.current.hide();
                              }

                              break;
                      }
                  }
              },
              [
                  onChange
              ]
          ),
          onMatchMobileChangeHandler          = () => {
              setMobile( getMobile() );
          },
          onFieldLeft                         = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> | React.KeyboardEvent<HTMLElement> | React.SyntheticEvent | undefined ) => {
                  const overlayElement = dropdownComponentRef.current?.getOverlay();

                  if ( overlayElement ) {
                      const activeElement = document.activeElement;

                      if ( overlayElement !== activeElement && !overlayElement.contains( activeElement ) ) {
                          dropdownComponentRef.current!.hide();
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownChange                    = useCallback(
              ( event : DropdownChangeEvent ) => {
                  let newValue = '';

                  if ( event.value ) {
                      newValue = event.value;
                  }

                  if ( dropdownComponentRef.current ) {
                      DomHandler.focus( dropdownComponentRef.current.getElement() );
                  }

                  onChange( newValue );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownShow                      = useCallback(
              () => {
                  if ( fieldLeftTimeoutId.current ) {
                      clearTimeout( fieldLeftTimeoutId.current );
                  }

                  setOverlay( true );
                  setRenderTrigger( renderTrigger + 1 );
              },
              [
                  renderTrigger
              ]
          ),
          onDropdownHide                      = useCallback(
              () => {
                  setOverlay( false );
              },
              [
                  'onlyOnce'
              ]
          ),
          onDropdownFocus                     = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                  setFocus( true );

                  if ( fieldLeftTimeoutId.current ) {
                      clearTimeout( fieldLeftTimeoutId.current );
                  }

                  onFocus?.( event );
              },
              [
                  onFocus
              ]
          ),
          onDropdownBlur                      = useCallback(
              ( event : React.FocusEvent<HTMLInputElement> | React.FocusEvent<Element> ) => {
                  if ( !triggerJustFocused.current ) {
                      setFocus( false );

                      if ( fieldLeftTimeoutId.current ) {
                          clearTimeout( fieldLeftTimeoutId.current );
                      }
                      fieldLeftTimeoutId.current = setTimeout(
                          onFieldLeft.bind( null, event ),
                          35
                      );

                      onBlur?.( event );
                  }
                  else {
                      triggerJustFocused.current = false;
                  }
              },
              [
                  onBlur
              ]
          ),
          onDropdownKeyDown                   = useCallback(
              ( event : React.KeyboardEvent<HTMLDivElement> ) => {
                  if ( dropdownComponentRef.current ) {
                      switch ( event.key ) {
                          case 'Enter':
                              dropdownComponentRef.current.focus();
                              break;
                      }
                  }
              },
              [
                  'onlyOnce'
              ]
          ),
          onTriggerFocus                      = useCallback(
              ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                  triggerJustFocused.current = true;
                  onDropdownFocus( event );
              },
              [
                  onDropdownFocus
              ]
          ),
          onTriggerBlur                       = useCallback(
              ( element : HTMLElement, event : React.FocusEvent<HTMLElement> ) => {
                  triggerJustFocused.current = false;
                  onDropdownBlur( event );
              },
              [
                  onDropdownBlur
              ]
          ),
          content                             = useMemo(
              () => {
                  return <div className={className + ' field'} style={style}>
                      {label && !mobile &&
                          <label
                              htmlFor={`${uid}_schemaDropdown_i`}
                              className="block"
                          >
                              {label}
                          </label>
                      }
                      <Dropdown
                          id={`${uid}_schemaDropdown`}
                          key={`${uid}_schemaDropdown`}
                          ref={dropdownComponentRef}

                          inputId={`${uid}_schemaDropdown_i`}

                          value={value}
                          options={data}

                          onChange={onDropdownChange}
                          onShow={onDropdownShow}
                          onHide={onDropdownHide}
                          onFocus={onDropdownFocus}
                          onBlur={onDropdownBlur}
                          onKeyDown={onDropdownKeyDown}

                          className={dropdownClassNames.join( ' ' )}
                          placeholder={mobile && label ? label : __( 'YN.base.finder.schema' )}
                          disabled={data.length < 2}
                          style={inputStyle}
                          tabIndex={tabIndex}

                          {...dropdownProps}
                      />
                  </div>;
              },
              [
                  className,
                  data,
                  dropdownClassNames,
                  dropdownProps,
                  inputStyle,
                  label,
                  language,
                  mobile,
                  onDropdownBlur,
                  onDropdownChange,
                  onDropdownFocus,
                  onDropdownHide,
                  onDropdownKeyDown,
                  onDropdownShow,
                  style,
                  tabIndex,
                  uid,
                  value
              ]
          );

    // on mount
    useEffect(
        () => {
            matchMedia( 'screen and (max-width: 960px)' ).addEventListener( 'change', onMatchMobileChangeHandler );
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            document.addEventListener( 'keydown', onDocumentKeyDown, true );
        },
        []
    );

    // on unmount
    useEffect(
        () => {
            return () => {
                matchMedia( 'screen and (max-width: 960px)' ).removeEventListener( 'change', onMatchMobileChangeHandler );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                document.removeEventListener( 'keydown', onDocumentKeyDown, true );
            };
        },
        []
    );

    // set tabIndex to filter if
    useLayoutEffect(
        () => {
            if ( tabIndex && dropdownComponentRef.current ) {
                const overlayTarget      = dropdownComponentRef.current.getOverlay(),
                      target             = dropdownComponentRef.current.getElement(),
                      overlayFilterInput = overlayTarget?.querySelector( 'input.p-dropdown-filter' ) as HTMLInputElement | undefined,
                      targetDownTrigger  = target?.querySelector( 'div.p-dropdown-trigger' ) as HTMLElement | undefined;

                overlayFilterInput?.setAttribute( 'tabindex', `${tabIndex + 1}` );

                targetDownTrigger?.setAttribute( 'tabindex', `${tabIndex + 3}` );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.removeEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.removeEventListener( 'blur', onTriggerBlur );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.addEventListener( 'focus', onTriggerFocus );
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                targetDownTrigger?.addEventListener( 'blur', onTriggerBlur );
            }
        }
    );

    return content;
};

SchemaDropdown.defaultProps = defaultProps;
SchemaDropdown.displayName = 'SchemaDropdown';

export default SchemaDropdown;
